import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import Login from "./components/Login2";
import UserAccess from "./pages/UserAccess";
import ProcessDocument from "./pages/ProcessDocument";
import OldViewPage from "./pages/ViewPage";
import HomePage from "./pages/IndexPage2";
import UnderConstruction from "./pages/Underconstruction";
import ErrorBoundary from "./CommonComponents/ErrorBoundary";
import { useIdleTimer } from "react-idle-timer";
import { IdealTimeForLogOut } from "./variable";
import { useCookies } from "react-cookie";
import { axiosRequest } from "./axiosConfig";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import IdleWarningDialog from "./components/IdleWarningDialog";

// Create a client
const queryClient = new QueryClient();

export const hasAzureSSOAccess = process.env.REACT_APP_HAS_AZURE_SSO == "true";
export const isDemo = process.env.REACT_APP_IS_DEMO == "true";
export const hasNikoHealthLogin =
  process.env.REACT_APP_HAS_NIKOHEALTH_LOGIN == "true";

function App() {
  const [cookies, setCookies, removeCookies] = useCookies();
  let initialCall = false;
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const idleWarningTimeout = useRef(null);

  // This function is release the lock for document.
  const releaseLockApi = async (props, e) => {
    await axiosRequest
      .get(`${process.env.REACT_APP_BaseUrl}releaselocks`)
      .then((res) => {
        if (props == "idl") {
          if (!initialCall) {
            removeCookies();
          }
          initialCall = true;
          window?.location?.replace(`${window?.location?.origin}`);
        }
        if (props == "close") {
          const message = "o/";
          (e || window.event).returnValue = message; //Gecko + IE
          return message;
        }
      })
      .catch((err) => {
        console.log(err, "releaselocks");
      });
  };

  // on Idle the release lock function called.
  const onIdle = () => {
    if (window?.location?.pathname !== "/") {
      setShowIdleWarning(true);
      // Give user 1 minute to respond before logging out
      if (idleWarningTimeout.current) {
        clearTimeout(idleWarningTimeout.current);
      }
      idleWarningTimeout.current = setTimeout(() => {
        if (isIdle()) {
          releaseLockApi("idl");
        }
      }, 60000); // 1 minute warning
    }
  };

  // on Active the release lock function called.
  const onActive = (event) => {
    if (window?.location?.pathname !== "/") {
      setShowIdleWarning(false);
      if (idleWarningTimeout.current) {
        clearTimeout(idleWarningTimeout.current);
        idleWarningTimeout.current = null;
      }
      releaseLockApi("idl");
    }
  };

  const handleStayActive = () => {
    setShowIdleWarning(false);
    if (idleWarningTimeout.current) {
      clearTimeout(idleWarningTimeout.current);
      idleWarningTimeout.current = null;
    }
    activate(); // Reset the idle timer
  };

  const handleLogout = () => {
    setShowIdleWarning(false);
    if (idleWarningTimeout.current) {
      clearTimeout(idleWarningTimeout.current);
      idleWarningTimeout.current = null;
    }
    releaseLockApi("idl");
  };

  // This is the react-idle-timer's functionality where this library will get the Idle time,
  // and many more functionality.
  const {
    start,
    reset,
    activate,
    pause,
    resume,
    isIdle,
    isPrompted,
    isLeader,
    getTabId,
    getRemainingTime,
    getElapsedTime,
    getLastIdleTime,
    getLastActiveTime,
    getTotalIdleTime,
    getTotalActiveTime,
  } = useIdleTimer({
    onIdle,
    onActive,
    timeout: 1000 * 60 * IdealTimeForLogOut,
    promptTimeout: 0,
    events: [
      "mousemove",
      "keydown",
      "wheel",
      "DOMMouseScroll",
      "mousewheel",
      "mousedown",
      "touchstart",
      "touchmove",
      "MSPointerDown",
      "MSPointerMove",
    ],
    immediateEvents: [],
    debounce: 0,
    throttle: 0,
    eventsThrottle: 2000,
    element: document,
    startOnMount: true,
    startManually: false,
    stopOnIdle: false,
    crossTab: false,
    name: "idle-timer",
    syncTimers: 0,
    leaderElection: false,
  });

  // on Browser close and tab close
  useEffect(() => {
    window.onunload = function (e) {
      releaseLockApi("close", e);
    };
    window.onload = function (e) {
      releaseLockApi("close", e);
    };
    return () => {
      window.onunload = function () {};
      window.onload = function () {};
      if (idleWarningTimeout.current) {
        clearTimeout(idleWarningTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    // Check if we're on the domain that needs to be redirected
    if (window.location.hostname === 'intelligent-intake.celeritasai.com') {
      // Redirect to the new domain, preserving the path and query parameters
      window.location.href = 'https://intelligent-intake.app.celeritasai.com' +
                            window.location.pathname +
                            window.location.search;
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <Router>
          <Switch>
            <Route exact path="/" component={Login} />
            <ErrorBoundary>
              <Route path="/home" component={HomePage} />
              <Route path="/user-access" component={UserAccess} />
              <Route path="/viewpage" component={OldViewPage} />
              <Route path="/processview" component={ProcessDocument} />
              <Route path="/U-Construction" component={UnderConstruction} />
            </ErrorBoundary>
          </Switch>
          <IdleWarningDialog
            isVisible={showIdleWarning}
            onStayActive={handleStayActive}
            onLogout={handleLogout}
          />
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
